const escapeRegex = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const collectText = (value) => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(collectText).join(' ');
  if (value && typeof value === 'object') return Object.values(value).map(collectText).join(' ');
  return '';
};

const extractTextFromBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return '';
  return blocks
    .map((block) => collectText(block?.data))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeArticleBody = (articleBody) => {
  if (typeof articleBody === 'string') {
    try {
      return JSON.parse(articleBody);
    } catch (_error) {
      return { blocks: [] };
    }
  }
  if (articleBody && typeof articleBody === 'object') {
    return articleBody;
  }
  return { blocks: [] };
};

const isPolish = (language) => /^(pl|pol)/i.test(String(language || '').trim());

// Polish is inflected ("buty do biegania" -> "butów do biegania"), so each
// word matches by stem plus an ending; English allows a plural/possessive.
const keywordTokenPattern = (token, polish) => {
  const escaped = escapeRegex(token);
  if (polish && token.length >= 3) {
    const stemLength = Math.max(3, token.length <= 4 ? token.length - 1 : token.length - 2);
    return `${escapeRegex(token.slice(0, stemLength))}\\p{L}{0,4}`;
  }
  if (!polish && /^\p{L}{3,}$/u.test(token)) {
    return `${escaped}(?:s|es|'s)?`;
  }
  return escaped;
};

const countOccurrences = (text, keyword, language) => {
  const tokens = String(keyword || '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return 0;
  const polish = isPolish(language);
  const body = tokens.map((token) => keywordTokenPattern(token, polish)).join('\\s+');
  const matches = text.match(new RegExp(`(?<![\\p{L}\\p{N}])${body}(?![\\p{L}\\p{N}])`, 'giu'));
  return matches ? matches.length : 0;
};

// Density window counts inflected forms; above 2% reads as keyword stuffing.
const isIdealDensity = (density) => density >= 0.5 && density <= 2;

const checkContentQuality = (article, keywords) => {
  const articleBody = normalizeArticleBody(article?.article_body);
  const blocks = Array.isArray(articleBody?.blocks) ? articleBody.blocks : [];
  const articleContent = extractTextFromBlocks(blocks);
  const wordCount = articleContent.split(/\s+/).filter((word) => word.length > 0).length;

  const isSummary = article?.content_type === 'summary';
  // A changelog is a short release note: no keyword targets, short is good.
  const isChangelog = article?.content_type === 'changelog';
  const hasKeywords = !isChangelog && Array.isArray(keywords) && keywords.length > 0;

  let keywordCounts = {};
  let keywordDensity = 0;
  let primaryKeyword = '';

  if (hasKeywords) {
    keywordCounts = keywords.reduce((acc, keyword) => {
      acc[keyword] = countOccurrences(articleContent, keyword, article?.language);
      return acc;
    }, {});
    primaryKeyword = String(keywords[0] || '');
    const keywordCount = keywordCounts[primaryKeyword] || 0;
    keywordDensity = wordCount > 0 ? (keywordCount / wordCount) * 100 : 0;
  }

  const titleBlock = blocks.find((block) => block?.type === 'header' && Number(block?.data?.level) === 1);
  const titleText = titleBlock?.data?.text || article?.title || '';
  const titleLength = String(titleText).length;

  let wordCountScore = 10;
  if (isChangelog) {
    if (wordCount >= 60 && wordCount <= 300) wordCountScore = 30;
    else if (wordCount >= 30 && wordCount <= 450) wordCountScore = 20;
  } else if (isSummary) {
    if (wordCount >= 350 && wordCount <= 450) wordCountScore = 30;
    else if (wordCount >= 300 && wordCount <= 500) wordCountScore = 25;
    else if (wordCount >= 250 && wordCount <= 550) wordCountScore = 20;
    else if (wordCount >= 200 && wordCount <= 600) wordCountScore = 15;
  } else {
    if (wordCount > 1000) wordCountScore = 30;
    else if (wordCount >= 750) wordCountScore = 25;
    else if (wordCount >= 500) wordCountScore = 20;
    else if (wordCount >= 250) wordCountScore = 15;
  }

  const headingsCounts = { H1: 0, H2: 0, H3: 0 };
  blocks.forEach((block) => {
    if (block?.type !== 'header') return;
    const level = Number(block?.data?.level);
    if (level >= 1 && level <= 3) {
      headingsCounts[`H${level}`] += 1;
    }
  });

  let keywordDensityScore = 0;
  if (hasKeywords) {
    keywordDensityScore = isIdealDensity(keywordDensity) ? 20 : keywordDensity > 0 && keywordDensity < 3 ? 10 : 0;
  }

  const isIdealTitle = isChangelog
    ? titleLength >= 20 && titleLength <= 70
    : titleLength >= 50 && titleLength <= 60;
  const titleLengthScore = isIdealTitle ? 20 : titleLength >= 40 && titleLength <= 70 ? 15 : 10;

  let headingsScore = 0;
  if (headingsCounts.H1 === 1) headingsScore += 10;
  if (headingsCounts.H2 > 0) headingsScore += 10;
  // Changelog sections are flat bullet lists; H3 is not expected there.
  if (headingsCounts.H3 > 0 || (isChangelog && headingsCounts.H2 > 0)) headingsScore += 10;

  const maxPossibleScore = hasKeywords ? 100 : 80;
  const overallScore = wordCountScore + keywordDensityScore + titleLengthScore + headingsScore;
  const overallPercentage = Math.round((overallScore / maxPossibleScore) * 100);

  const report = {
    quality_control_report: {
      'Overall Score': {
        score: overallScore,
        maxScore: maxPossibleScore,
        percentage: `${overallPercentage}%`,
        mode: isChangelog ? 'changelog' : isSummary ? 'summary' : 'article'
      },
      'Word Count': {
        description: isChangelog
          ? 'Determine the total word count of the changelog. Ideal: 60-300 words. Maximum score: 30/30.'
          : isSummary
            ? 'Determine the total word count of the summary. Ideal: 350-450 words. Maximum score: 30/30.'
            : 'Determine the total word count of the article. Ideal: 800-1200 words. Maximum score: 30/30.',
        count: wordCount,
        score: wordCountScore,
        maxScore: 30,
        ideal: isChangelog ? '60-300 words' : isSummary ? '350-450 words' : '800-1200 words'
      },
      'Title Length': {
        description: isChangelog
          ? 'Measure the character count of the title. Ideal: 20-70 characters. Maximum score: 20/20.'
          : 'Measure the character count of the title. Ideal: 50-60 characters. Maximum score: 20/20.',
        length: titleLength,
        isIdeal: isIdealTitle ? 'Yes' : 'No',
        score: titleLengthScore,
        maxScore: 20
      },
      'Headings Count': {
        description: 'Count the number of headings used in the article. One H1: 10, any H2: 10, any H3: 10. Maximum score: 30/30.',
        counts: headingsCounts,
        isH1Correct: headingsCounts.H1 === 1 ? 'Yes' : 'No',
        score: headingsScore,
        maxScore: 30
      }
    }
  };

  if (hasKeywords) {
    report.quality_control_report['Keyword Check'] = {
      description: 'Count occurrences of each keyword throughout the article.',
      counts: keywordCounts
    };
    report.quality_control_report['Keyword Density'] = {
      description: 'Calculate keyword density for the primary keyword (inflected forms count). Ideal: 0.5-2%.',
      density: `${keywordDensity.toFixed(2)}%`,
      isIdeal: isIdealDensity(keywordDensity) ? 'Yes' : 'No',
      score: keywordDensityScore,
      maxScore: 20
    };
  }

  return [report];
};

export default checkContentQuality;
